import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faFileExcel, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useToast } from '../ui/ToastProvider';
import { importBrands, BrandImportResult } from '../../services/brandApi';

interface BrandImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

const BrandImportModal: React.FC<BrandImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const { showSuccess, showError } = useToast();
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Validate file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(fileExtension || '')) {
      showError('Nur Excel (.xlsx, .xls) oder CSV-Dateien sind erlaubt');
      return;
    }

    await processFile(file);
  };

  const processFile = async (file: File) => {
    try {
      setImporting(true);

      // For this example, we'll simulate parsing and create brand data
      // In a real implementation, you'd parse the Excel/CSV file here
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          // Simple CSV parsing for brands (assuming one brand per line)
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim() !== '');
          
          // Skip header if it exists
          const brands = lines.slice(1).map(line => {
            const brandName = line.split(',')[0]?.trim().replace(/"/g, '');
            return { name: brandName };
          }).filter(brand => brand.name);

          if (brands.length === 0) {
            showError('Keine gültigen Marken in der Datei gefunden');
            return;
          }

          // Import brands
          const result = await importBrands(brands);
          
          if (result.successful > 0) {
            showSuccess(`${result.successful} Marken erfolgreich importiert`);
            onImportComplete();
            onClose();
          } else {
            showError('Keine Marken konnten importiert werden');
          }

          if (result.errors.length > 0) {
            console.log('Import errors:', result.errors);
          }

        } catch (error) {
          console.error('Error processing file:', error);
          showError('Fehler beim Verarbeiten der Datei');
        } finally {
          setImporting(false);
        }
      };

      reader.readAsText(file);

    } catch (error) {
      console.error('Error importing brands:', error);
      showError('Fehler beim Importieren der Marken');
      setImporting(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faUpload} className="text-blue-600 text-2xl mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Marken importieren</h2>
              <p className="text-sm text-gray-500">CSV oder Excel-Datei mit Marken hochladen</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={importing}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Instructions */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Anleitung</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2">
                Format der Datei:
              </p>
              <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                <li>CSV oder Excel (.xlsx, .xls) Datei</li>
                <li>Erste Spalte: Markenname</li>
                <li>Eine Marke pro Zeile</li>
                <li>Optional: Header-Zeile (wird übersprungen)</li>
              </ul>
            </div>
          </div>

          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            } ${importing ? 'opacity-50 pointer-events-none' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {importing ? (
              <div className="flex flex-col items-center">
                <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-500 mb-4 animate-spin" />
                <p className="text-lg font-medium text-gray-700">Importiere Marken...</p>
                <p className="text-sm text-gray-500 mt-2">Bitte warten Sie, während die Datei verarbeitet wird.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <FontAwesomeIcon icon={faFileExcel} className="text-4xl text-green-500 mb-4" />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Datei hier ablegen oder klicken zum Auswählen
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Unterstützte Formate: .xlsx, .xls, .csv
                </p>
                <label className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                  <FontAwesomeIcon icon={faUpload} className="mr-2" />
                  Datei auswählen
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={importing}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Examples */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Beispiel CSV-Format:</h4>
            <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm font-mono text-gray-700">
              Marke<br/>
              Coca-Cola<br/>
              Pepsi<br/>
              Red Bull<br/>
              Haribo<br/>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            disabled={importing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandImportModal;
